import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api, errMsg, fileUrl, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CitySelect } from "@/components/CitySelect";
import { DatePicker } from "@/components/DatePicker";
import { ProviderLogo, VerifiedBadge } from "@/components/ProviderCard";
import { RatingSummary } from "@/components/Stars";
import { PageLoader } from "@/components/layout/RequireAuth";

export default function BookPackage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState(null);
  const [f, setF] = useState({ scheduled_date: "", city: "", address: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/packages/${packageId}`).then((r) => setPkg(r.data)).catch(() => navigate("/"));
  }, [packageId, navigate]);
  if (!pkg) return <PageLoader />;
  const p = pkg.provider;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.scheduled_date || !f.city) return toast.error("Pilih tanggal dan kota");
    setSaving(true);
    try {
      const { data } = await api.post("/orders", { package_id: packageId, ...f });
      toast.success("Pesanan dibuat. Lanjutkan ke pembayaran.");
      navigate(`/bayar?pesanan=${data.order_id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-page max-w-5xl py-10 lg:py-14" data-testid="book-package-page">
      <p className="eyebrow">Pesan Langsung</p>
      <h1 className="mt-2 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">{pkg.title}</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <form onSubmit={submit} className="card-soft space-y-5 p-6 sm:p-8" data-testid="book-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tanggal pengerjaan</Label>
              <DatePicker value={f.scheduled_date} onChange={(v) => setF({ ...f, scheduled_date: v })} testId="book-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Kota</Label>
              <CitySelect cities={p.city_items} value={f.city} onChange={(v) => setF({ ...f, city: v })} required testId="book-city-select" />
              <p className="text-xs text-stone-500">Hanya kota layanan {p.business_name}.</p>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Alamat lengkap</Label>
            <Input required minLength={5} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="h-11 rounded-xl" data-testid="book-address-input" />
          </div>
          <div className="space-y-2">
            <Label>Catatan (opsional)</Label>
            <Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-xl" data-testid="book-notes-input" />
          </div>
          <Button type="submit" disabled={saving} className="h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid="book-submit-button">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Buat Pesanan · {rupiah(pkg.price)}
          </Button>
        </form>
        <aside className="card-soft self-start overflow-hidden">
          {pkg.photo_path && <img src={fileUrl(pkg.photo_path)} alt="" className="h-40 w-full object-cover" />}
          <div className="space-y-4 p-6">
            <div className="flex items-center gap-3">
              <ProviderLogo provider={p} size="h-10 w-10" />
              <div>
                <p className="text-sm font-bold text-stone-900">{p.business_name}</p>
                <div className="flex items-center gap-2">{p.verified && <VerifiedBadge />}<RatingSummary rating={p.rating} count={p.review_count} className="text-xs" /></div>
              </div>
            </div>
            <p className="text-3xl font-extrabold text-stone-900" data-testid="book-price">{rupiah(pkg.price)}</p>
            <p className="text-sm text-stone-600">{pkg.description}</p>
            <ul className="space-y-1.5">{pkg.includes.map((i) => <li key={i} className="flex gap-2 text-sm text-stone-600"><Check className="mt-0.5 h-4 w-4 text-teal-600" />{i}</li>)}</ul>
            <p className="rounded-xl bg-stone-50 p-3 text-xs text-stone-500">Pembayaran ditahan Tuntas dan baru diteruskan ke penyedia setelah Anda mengonfirmasi pekerjaan selesai.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
